"use client";

import { useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  ROLE_CONFIG,
  type SDKClass,
  type SDKLanguage,
  type SDKCategory,
  type SDKMethod,
  type MethodRole,
} from "../../lib/sdk";
import CopyButton from "./CopyButton";

const RulePlayground = dynamic(() => import("../../components/RulePlayground"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full bg-gray-950">
      <div className="text-gray-500 text-sm font-mono">Loading playground...</div>
    </div>
  ),
});

// Hardcoded playground code per class. Populated as classes get example repos.
const PLAYGROUND_CODE: Record<string, { code: string; command: string }> = {
  GoGinContext: {
    command: "pathfinder scan --ruleset golang/security --project .",
    code: `# --- file: handlers.go ---
package main

import (
\t"github.com/gin-gonic/gin"
\t"gorm.io/gorm"
)

// VULNERABILITY: c.Query("id") flows into db.Raw() — SQL injection
func GetUser(db *gorm.DB, c *gin.Context) {
\tuserID := c.Query("id")                                         // SOURCE
\tvar user map[string]interface{}
\tdb.Raw("SELECT * FROM users WHERE id = " + userID).Scan(&user) // SINK
\tc.JSON(200, user)
}

// VULNERABILITY: c.Query("q") flows into db.Raw()
func SearchProducts(db *gorm.DB, c *gin.Context) {
\tterm := c.Query("q")                                            // SOURCE
\tdb.Raw("SELECT * FROM products WHERE name LIKE '%" + term + "%'") // SINK
}

// VULNERABILITY: c.Param("id") flows into db.Exec()
func DeleteSession(db *gorm.DB, c *gin.Context) {
\tuserID := c.Param("id")                                         // SOURCE
\tdb.Exec("DELETE FROM sessions WHERE user_id = " + userID)      // SINK
}

# --- file: go.mod ---
module example.com/gin-sqli-demo

go 1.21.0

require (
\tgithub.com/gin-gonic/gin v1.9.1
\tgorm.io/gorm v1.25.7
)
`,
  },
};

interface Props {
  cls: SDKClass;
  language: SDKLanguage;
  lang: string;
  catId: string;
  categoryDef: SDKCategory | undefined;
  prev: SDKClass | null;
  next: SDKClass | null;
}

function RoleBadge({ role }: { role: MethodRole }) {
  const cfg = ROLE_CONFIG[role];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function TaintFlowDiagram({ cls }: { cls: SDKClass }) {
  const sources = cls.methods.filter((m) => m.role === "source");
  const sinks = cls.methods.filter((m) => m.role === "sink");
  const sanitizers = cls.methods.filter((m) => m.role === "sanitizer");

  if (sources.length === 0 && sinks.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden mb-8">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Taint flow</span>
        <span className="text-xs text-gray-400">
          {sources.length} source{sources.length !== 1 ? "s" : ""} →
          {sanitizers.length > 0 ? ` ${sanitizers.length} sanitizer${sanitizers.length !== 1 ? "s" : ""} →` : ""}
          {" "}{sinks.length} sink{sinks.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="p-5 flex gap-4 overflow-x-auto">
        {/* Sources column */}
        {sources.length > 0 && (
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              Sources — untrusted input
            </div>
            <div className="flex flex-col gap-2">
              {sources.map((m) => (
                <div key={m.name} className="flex items-center gap-2 group/method">
                  <code className="text-xs font-mono px-2.5 py-1.5 rounded-lg bg-green-50 border border-green-200 text-green-800 whitespace-nowrap">
                    .{m.name}()
                  </code>
                  <span className="text-xs text-gray-400 truncate hidden sm:block">{m.description.split(".")[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Arrow */}
        {sources.length > 0 && sinks.length > 0 && (
          <div className="flex flex-col items-center justify-center gap-1.5 px-2 flex-shrink-0">
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-1">
              <div className="w-12 h-px bg-gradient-to-r from-green-300 via-gray-300 to-red-300" />
              <span className="text-red-400 text-xs">→</span>
            </div>
            <span className="text-xs text-gray-400 font-mono">taint</span>
            <div className="w-px h-4 bg-gray-200" />
          </div>
        )}

        {/* Sanitizers column */}
        {sanitizers.length > 0 && (
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              Sanitizers — blocks taint
            </div>
            <div className="flex flex-col gap-2">
              {sanitizers.map((m) => (
                <code key={m.name} className="text-xs font-mono px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 whitespace-nowrap self-start">
                  .{m.name}()
                </code>
              ))}
            </div>
          </div>
        )}

        {/* Sinks column */}
        {sinks.length > 0 && (
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
              Sinks — dangerous call
            </div>
            <div className="flex flex-col gap-2">
              {sinks.map((m) => (
                <div key={m.name} className="flex items-center gap-2">
                  <code className="text-xs font-mono px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-800 whitespace-nowrap">
                    .{m.name}()
                  </code>
                  <span className="text-xs text-gray-400 truncate hidden sm:block">{m.description.split(".")[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MethodCard({ method, clsName }: { method: SDKMethod; clsName: string }) {
  const selector = `${clsName}.method("${method.name}")`;

  return (
    <div id={`method-${method.name}`} className="group scroll-mt-20 rounded-xl border border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm transition-all overflow-hidden">
      {/* Method header */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          <code className="text-sm font-bold text-gray-900 font-mono">.{method.name}()</code>
          <RoleBadge role={method.role} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <CopyButton text={selector} />
          <a
            href={`#method-${method.name}`}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 text-sm"
          >
            #
          </a>
        </div>
      </div>

      {/* Signature */}
      <div className="mx-4 mb-3 rounded-lg border border-gray-100 overflow-hidden">
        <div className="px-3 py-1 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">Signature</span>
          <CopyButton text={method.signature} />
        </div>
        <pre className="px-3 py-2.5 text-xs font-mono text-gray-700 bg-white overflow-x-auto">
          {method.signature}
        </pre>
      </div>

      {/* Description + meta */}
      <div className="px-4 pb-4">
        <p className="text-sm text-gray-600 mb-2.5">{method.description}</p>

        {(method.tracks.length > 0 || method.where_example) && (
          <div className="flex flex-wrap gap-2">
            {method.tracks.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 px-2.5 py-1 rounded-lg">
                <span className="font-medium text-gray-400">tracks:</span>
                <code className="font-mono">{method.tracks.join(", ")}</code>
              </div>
            )}
            {method.where_example && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 px-2.5 py-1 rounded-lg">
                <span className="font-medium text-gray-400">.where():</span>
                <code className="font-mono">{method.where_example}</code>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MethodGroup({
  role,
  methods,
  clsName,
}: {
  role: MethodRole;
  methods: SDKMethod[];
  clsName: string;
}) {
  if (methods.length === 0) return null;

  const labels: Record<MethodRole, { heading: string; subtitle: string; dot: string; text: string }> = {
    source: { heading: "Sources", subtitle: "user-controlled input enters here", dot: "bg-green-500", text: "text-green-700" },
    sink: { heading: "Sinks", subtitle: "dangerous functions taint must not reach", dot: "bg-red-500", text: "text-red-700" },
    sanitizer: { heading: "Sanitizers", subtitle: "functions that neutralize taint", dot: "bg-blue-500", text: "text-blue-700" },
    neutral: { heading: "Other Methods", subtitle: "", dot: "bg-gray-400", text: "text-gray-600" },
  };

  const cfg = labels[role];

  // Copy all selectors for this role group
  const allSelectors = `${clsName}.method(${methods.map((m) => `"${m.name}"`).join(", ")})`;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
          <h2 className={`text-sm font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.heading}</h2>
          {cfg.subtitle && <span className="text-xs text-gray-400 hidden sm:block">— {cfg.subtitle}</span>}
        </div>
        <CopyButton text={allSelectors} />
      </div>
      <div className="space-y-3">
        {methods.map((m) => (
          <MethodCard key={m.name} method={m} clsName={clsName} />
        ))}
      </div>
    </div>
  );
}

export default function ClassDetailTabs({
  cls,
  language,
  lang,
  catId,
  categoryDef,
  prev,
  next,
}: Props) {
  const [activeTab, setActiveTab] = useState<"reference" | "playground">("reference");
  const playgroundData = PLAYGROUND_CODE[cls.id];

  const sources    = cls.methods.filter((m) => m.role === "source");
  const sinks      = cls.methods.filter((m) => m.role === "sink");
  const sanitizers = cls.methods.filter((m) => m.role === "sanitizer");
  const neutrals   = cls.methods.filter((m) => m.role === "neutral");
  const orderedMethods = [...sources, ...sinks, ...sanitizers, ...neutrals];

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      {/* Sticky breadcrumb + tab bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 flex items-center px-8">
        <div className="flex items-center gap-1.5 text-sm font-mono text-gray-500 py-3 flex-wrap mr-8">
          <Link href="/sdk" className="hover:text-gray-900">sdk</Link>
          <span>/</span>
          <Link href={`/sdk/${lang}`} className="hover:text-gray-900">{lang}</Link>
          <span>/</span>
          <Link href={`/sdk/${lang}/${catId}`} className="hover:text-gray-900">{categoryDef?.name ?? catId}</Link>
          <span>/</span>
          <span className="text-gray-900 font-semibold">{cls.name}</span>
        </div>

        <div className="ml-auto flex items-center">
          {(["reference", "playground"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-green-600 text-green-700"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {tab === "playground" && <span className="mr-1.5 text-xs">⚡</span>}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── REFERENCE TAB ───────────────────────────────────────────────── */}
      {activeTab === "reference" && (
        <div className="flex flex-1 min-h-0">
          {/* Main content */}
          <div className="flex-1 min-w-0 overflow-y-auto">
            <div className="max-w-2xl px-8 py-8">
              {/* Class header */}
              <div className="mb-7">
                {categoryDef && (
                  <Link
                    href={`/sdk/${lang}/${catId}`}
                    className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mb-3 hover:bg-green-50 hover:text-green-700 transition-colors"
                  >
                    {categoryDef.name}
                  </Link>
                )}
                <h1 className="text-2xl font-bold text-gray-900 font-mono mb-2">{cls.name}</h1>
                <p className="text-sm text-gray-600 leading-relaxed">{cls.description}</p>

                <div className="flex flex-wrap gap-2 mt-4">
                  {sources.length > 0 && (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      {sources.length} source{sources.length > 1 ? "s" : ""}
                    </span>
                  )}
                  {sinks.length > 0 && (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      {sinks.length} sink{sinks.length > 1 ? "s" : ""}
                    </span>
                  )}
                  {sanitizers.length > 0 && (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      {sanitizers.length} sanitizer{sanitizers.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>

              {/* Taint flow diagram */}
              <TaintFlowDiagram cls={cls} />

              {/* Quick-start rule — the #1 thing rule writers need */}
              {cls.example_rule && (
                <div className="mb-10 rounded-xl overflow-hidden border border-gray-800 shadow-lg">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-950">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500/70" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                        <div className="w-3 h-3 rounded-full bg-green-500/70" />
                      </div>
                      <span className="text-xs text-gray-400 font-mono">Quick-start rule — copy and run</span>
                    </div>
                    <CopyButton text={cls.example_rule} />
                  </div>
                  <pre className="bg-gray-950 text-sm font-mono px-5 py-4 overflow-x-auto text-gray-300 leading-relaxed">
                    {cls.example_rule}
                  </pre>
                  <div className="px-4 py-2.5 bg-gray-900 border-t border-gray-800 flex items-center gap-3 flex-wrap">
                    <code className="text-xs font-mono text-green-400">
                      pathfinder scan --ruleset custom/security --project .
                    </code>
                    <button
                      onClick={() => setActiveTab("playground")}
                      className="ml-auto flex-shrink-0 text-xs font-medium text-gray-400 hover:text-green-400 transition-colors"
                    >
                      ⚡ Try in Playground →
                    </button>
                  </div>
                </div>
              )}

              {/* Methods grouped by role */}
              <div className="space-y-10 mb-10">
                <MethodGroup role="source"    methods={sources}    clsName={cls.name} />
                <MethodGroup role="sink"      methods={sinks}      clsName={cls.name} />
                <MethodGroup role="sanitizer" methods={sanitizers} clsName={cls.name} />
                <MethodGroup role="neutral"   methods={neutrals}   clsName={cls.name} />
              </div>

              {/* FQNs */}
              <section className="mb-8">
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Fully-Qualified Names
                </h2>
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">FQN</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-2.5 w-12" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {cls.fqns.map((fqn, i) => (
                        <tr key={fqn} className="hover:bg-gray-50 group">
                          <td className="px-4 py-3 font-mono text-sm text-gray-800">{fqn}</td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono">fqns[{i}]</td>
                          <td className="px-4 py-3">
                            <CopyButton text={fqn} />
                          </td>
                        </tr>
                      ))}
                      {cls.patterns.map((p) => (
                        <tr key={p} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm text-gray-500">{p}</td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono">patterns</td>
                          <td className="px-4 py-3">
                            <CopyButton text={p} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  Wrong FQN → 0 findings. Verify with: change <code className="font-mono">fqns</code> to garbage → must produce 0 results.
                </p>
              </section>

              {/* Import */}
              <section className="mb-8">
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Import</h2>
                <div className="space-y-2">
                  {cls.go_mod && (
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-400">go.mod</span>
                        <CopyButton text={cls.go_mod} />
                      </div>
                      <pre className="px-4 py-3 text-sm font-mono text-gray-800 bg-white overflow-x-auto">{cls.go_mod}</pre>
                    </div>
                  )}
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-400">rule.py</span>
                      <CopyButton text={cls.import_stmt} />
                    </div>
                    <pre className="px-4 py-3 text-sm font-mono text-gray-800 bg-white overflow-x-auto">{cls.import_stmt}</pre>
                  </div>
                </div>
              </section>

              {/* Rules using */}
              {cls.rules_using.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Rules Using This Class
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {cls.rules_using.map((ruleId) => (
                      <Link
                        key={ruleId}
                        href={`/registry/golang/security/${ruleId.toLowerCase()}`}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-mono text-gray-700 hover:border-green-400 hover:text-green-700 hover:bg-green-50 transition-all"
                      >
                        {ruleId}
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Prev / Next */}
              <div className="mt-10 pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
                <div>
                  {prev && (
                    <Link href={`/sdk/${lang}/${catId}/${prev.id}`} className="flex flex-col gap-0.5 group">
                      <span className="text-xs text-gray-400">← Previous</span>
                      <span className="text-sm font-mono font-semibold text-gray-700 group-hover:text-green-700">{prev.name}</span>
                    </Link>
                  )}
                </div>
                <div className="text-right">
                  {next && (
                    <Link href={`/sdk/${lang}/${catId}/${next.id}`} className="flex flex-col gap-0.5 items-end group">
                      <span className="text-xs text-gray-400">Next →</span>
                      <span className="text-sm font-mono font-semibold text-gray-700 group-hover:text-green-700">{next.name}</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right TOC */}
          <aside className="hidden xl:block w-52 flex-shrink-0 overflow-y-auto px-4 py-8 border-l border-gray-100">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">On this page</div>
            <nav className="space-y-0.5">
              {cls.example_rule && (
                <a href="#" className="block text-xs text-gray-600 hover:text-gray-900 py-1 font-medium">
                  Quick-start rule
                </a>
              )}
              {sources.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs text-green-700 font-semibold mb-1">Sources</div>
                  {sources.map((m) => (
                    <a key={m.name} href={`#method-${m.name}`} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 py-0.5 pl-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                      <code className="font-mono">.{m.name}()</code>
                    </a>
                  ))}
                </div>
              )}
              {sinks.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs text-red-700 font-semibold mb-1">Sinks</div>
                  {sinks.map((m) => (
                    <a key={m.name} href={`#method-${m.name}`} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 py-0.5 pl-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                      <code className="font-mono">.{m.name}()</code>
                    </a>
                  ))}
                </div>
              )}
              {sanitizers.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs text-blue-700 font-semibold mb-1">Sanitizers</div>
                  {sanitizers.map((m) => (
                    <a key={m.name} href={`#method-${m.name}`} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 py-0.5 pl-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                      <code className="font-mono">.{m.name}()</code>
                    </a>
                  ))}
                </div>
              )}
              {neutrals.map((m) => (
                <a key={m.name} href={`#method-${m.name}`} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 py-0.5 pl-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                  <code className="font-mono">.{m.name}()</code>
                </a>
              ))}
              <div className="pt-3 border-t border-gray-100 mt-3">
                <a href="#" className="block text-xs text-gray-500 hover:text-gray-900 py-0.5">FQNs</a>
                <a href="#" className="block text-xs text-gray-500 hover:text-gray-900 py-0.5">Import</a>
                {cls.rules_using.length > 0 && (
                  <a href="#" className="block text-xs text-gray-500 hover:text-gray-900 py-0.5">Rules using</a>
                )}
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* ── PLAYGROUND TAB ──────────────────────────────────────────────── */}
      {activeTab === "playground" && (
        <div className="flex-1 min-h-0">
          {playgroundData ? (
            <RulePlayground
              initialCode={playgroundData.code}
              initialQuery={cls.example_rule ?? ""}
              language={lang}
              command={playgroundData.command}
              height="calc(100vh - 57px)"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-5 bg-gray-50 text-center px-8">
              <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-2xl">⚡</div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Playground for <code className="font-mono">{cls.name}</code> coming soon
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  A live code example is being added for this class. Use the Reference tab to copy
                  the example rule and run it locally.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setActiveTab("reference")}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
                >
                  View Reference →
                </button>
                <a
                  href="https://github.com/shivasurya/code-pathfinder"
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white transition-colors"
                >
                  Contribute example
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
